import { useCallback, useEffect, useState } from 'react';

import {
  ContentPointQuestionType,
  ContentPointProficiencies,
  NSW,
} from 'global/constants';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import { PrimaryButton } from 'approot/shared/styles/buttons/primary-button';
import { LinkButton } from 'approot/shared/styles/buttons/link-button.style';
import { Heading16 } from 'approot/shared/styles/typography.style';
import Text from 'approot/shared/styles/typography/text';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import {
  ActivityOutcomeGroupEdit,
  ContentPoint,
  PROFICIENCIES,
} from './content-points.constants';
import { QUESTION_TYPES } from '../activity-outcomes.constants';
import { CheckableMulti } from 'approot/shared/styles/buttons/checkable-input';
import ListSearch, {
  ListSearchItem,
} from 'approot/shared/list-search/list-search';
import RemoveActivityOutcomeModal from './remove-activity-outcome-modal';
import ContentPointSearchByOutcome from './content-point-search-by-outcome';
import DeleteButtonIcon from 'approot/shared/styles/buttons/delete-icon-button';
import ContentPointsView from './content-points-view';
import QuestionTypesView from './question-types-view';
import ProficienciesView from './proficiencies-view';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import { ActivityOutcomeGroup } from '../activity-outcomes.constants';
import ButtonDismiss from 'approot/shared/design-system/button-dismiss';

type Props = {
  index: number;
  options: ListSearchItem[];
  selectedOptionValues: ListSearchItem[];
  isNew: boolean;
  data?: ActivityOutcomeGroup | null;
  isSaving?: boolean;
  isEditMode?: boolean;
  isItemBeingEdited?: boolean;
  currentCurriculum?: string;
  hideContentPointSearch?: boolean;
  hideQuestionTypes?: boolean;
  hideProficiencies?: boolean;
  setIsEditMode?: (isEditMode: boolean) => void;
  handleUpdateOutcome: (
    contentGroups: ActivityOutcomeGroupEdit,
    previousOutcomeId?: number
  ) => void;
  handleDeleteOutcome?: (contentGroups: ActivityOutcomeGroupEdit) => void;
  onCancel?: () => void;
};

const ContentPointGroupEdit = ({
  index,
  options,
  selectedOptionValues,
  data,
  isSaving,
  isEditMode,
  isItemBeingEdited,
  currentCurriculum,
  hideContentPointSearch,
  hideQuestionTypes,
  hideProficiencies,
  setIsEditMode,
  handleUpdateOutcome,
  handleDeleteOutcome,
  onCancel,
}: Props) => {
  const [selectedOutcome, setSelectedOutcome] = useState<ListSearchItem | null>(
    data?.outcome
      ? {
          id: data.outcomeId,
          label: data.outcome.code,
        }
      : null
  );

  const [selectedContentPoints, setSelectedContentPoints] = useState<
    ContentPoint[]
  >(
    data?.contentPoints.map(cp => ({
      id: cp.id,
      name: cp.name,
      content: cp.content,
    })) || []
  );

  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState<
    ContentPointQuestionType[]
  >(data?.questionTypes || []);

  const [selectedProficiencies, setSelectedProficiencies] = useState<
    ContentPointProficiencies[]
  >(data?.proficiencies || []);

  const {
    isVisible: isRemoveModalVisible,
    openModal: openRemoveModal,
    closeModal: closeRemoveModal,
  } = useSimpleModalState();

  const {
    isVisible: isDeleteModalVisible,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useSimpleModalState();

  const handleOutcomeSelected = (outcome: ListSearchItem | undefined) => {
    if (!outcome) return;
    setSelectedOutcome(outcome);
  };

  const handleOutcomeRemove = () => {
    if (selectedContentPoints.length > 0) {
      openRemoveModal();
    } else {
      handleConfirmOutcomeRemove();
    }
  };

  const handleConfirmOutcomeRemove = () => {
    setSelectedOutcome(null);
    setSelectedContentPoints([]);
    closeRemoveModal();
  };

  /**
   * Update the selected content points appending to the existing content points.
   * @param contentPoints
   */
  const handleUpdate = (contentPoints: ContentPoint[]) => {
    setSelectedContentPoints(contentPoints);
  };

  const handleSave = () => {
    const outcomeId = selectedOutcome?.id || data?.outcome.id;
    if (!outcomeId) return;

    handleUpdateOutcome(
      {
        contentPointIds: selectedContentPoints.map(c => c.id),
        questionTypes: selectedQuestionTypes,
        proficiencies: selectedProficiencies,
        cerTypes: [],
        outcomeId,
        parentOutcomeId: null,
      },
      data?.outcomeId
    );
  };

  const handleDeleteContentPoints = () => {
    const outcomeId = selectedOutcome?.id || data?.outcome.id;
    if (!outcomeId) return;
    handleDeleteOutcome?.({
      contentPointIds: selectedContentPoints.map(c => c.id),
      questionTypes: selectedQuestionTypes,
      proficiencies: selectedProficiencies,
      cerTypes: [],
      outcomeId,
      parentOutcomeId: null,
    });
  };

  const initialiseState = useCallback(() => {
    setSelectedOutcome(
      data?.outcome
        ? {
            id: data.outcome.id,
            label: data.outcome.code,
          }
        : null
    );
    setSelectedContentPoints(
      data?.contentPoints.map(cp => ({
        id: cp.id,
        name: cp.name,
        content: cp.content,
      })) || []
    );
    setSelectedQuestionTypes(data?.questionTypes || []);
    setSelectedProficiencies(data?.proficiencies || []);
  }, [data]);

  const handleCancel = () => {
    initialiseState();
    onCancel?.();
  };

  // Reset the state when the edit mode is disabled
  useEffect(() => {
    if (!isEditMode) {
      initialiseState();
    }
  }, [isEditMode, data, initialiseState]);

  if (!isEditMode) {
    const deleteButton = (
      <DeleteButtonIcon
        $isSmall
        $isDisabled={isItemBeingEdited}
        aria-label={`Delete group: ${selectedOutcome?.label}`}
        aria-haspopup="dialog"
        onClick={() => openDeleteModal()}
      />
    );

    const editButton = (
      <SecondaryButton
        type="button"
        $isSmall
        disabled={isItemBeingEdited}
        onClick={() => setIsEditMode?.(true)}
      >
        Edit
      </SecondaryButton>
    );

    return (
      <Box>
        {isDeleteModalVisible && (
          <RemoveActivityOutcomeModal
            isVisible={isDeleteModalVisible}
            isSaving={isSaving}
            onClose={closeDeleteModal}
            onConfirm={handleDeleteContentPoints}
          />
        )}
        <Box $justifyContent="space-between">
          <Box>
            <Heading16 as="h3" marginBottom={0}>
              Outcome
            </Heading16>
            <Text>{selectedOutcome?.label}</Text>
          </Box>
          {isItemBeingEdited ? (
            <TooltipWrapper
              multiline
              hoverText="You’ll need to save your current outcome before deleting another."
            >
              {deleteButton}
            </TooltipWrapper>
          ) : (
            deleteButton
          )}
        </Box>

        {selectedContentPoints.length > 0 && (
          <ContentPointsView contentPoints={selectedContentPoints} />
        )}

        {data?.questionTypes && data.questionTypes.length > 0 && (
          <QuestionTypesView questionTypes={data.questionTypes} />
        )}

        {data?.proficiencies && data.proficiencies.length > 0 && (
          <ProficienciesView proficiencies={data.proficiencies} />
        )}

        <Box $marginTop={16}>
          {isItemBeingEdited ? (
            <TooltipWrapper
              multiline
              hoverText="Finish entering your current outcome first to start editing here."
            >
              {editButton}
            </TooltipWrapper>
          ) : (
            editButton
          )}
        </Box>
      </Box>
    );
  }

  return (
    <SettingsGroup $marginBottom={0}>
      {isRemoveModalVisible && (
        <RemoveActivityOutcomeModal
          isVisible={isRemoveModalVisible}
          isSaving={isSaving}
          onClose={closeRemoveModal}
          onConfirm={handleConfirmOutcomeRemove}
        />
      )}

      <SettingsLabel>Outcomes</SettingsLabel>
      <ListSearch
        isMulti={false}
        tooltip={
          selectedOutcome !== null
            ? 'Almost there! Save this outcome before searching for another.'
            : undefined
        }
        fieldName="outcomeSearch"
        options={options}
        selectedOptionValues={selectedOptionValues}
        placeholder="Search"
        isDisabled={selectedOutcome !== null}
        onSelect={item =>
          handleOutcomeSelected(options.find(o => o.id === item.id))
        }
      />

      {selectedOutcome && (
        <Box $marginTop={16}>
          <ButtonDismiss onClick={() => handleOutcomeRemove()}>
            {selectedOutcome.label}
          </ButtonDismiss>
        </Box>
      )}

      {!hideContentPointSearch &&
        currentCurriculum?.toLowerCase() === NSW.toLowerCase() && (
          <Box $marginTop={16}>
            <ContentPointSearchByOutcome
              outcomeId={selectedOutcome?.id ?? null}
              selectedContentPoints={selectedContentPoints}
              onUpdate={handleUpdate}
            />
          </Box>
        )}

      {!hideQuestionTypes && (
        <SettingsGroup $marginTop={16} $marginBottom={0}>
          <SettingsLabel>Question Types</SettingsLabel>
          <CheckableMulti
            name={`questionTypes-${index}`}
            canDeselectAll
            options={QUESTION_TYPES}
            selected={selectedQuestionTypes}
            onSelect={values => {
              setSelectedQuestionTypes(values);
            }}
          />
        </SettingsGroup>
      )}

      {!hideProficiencies && (
        <SettingsGroup $marginTop={16}>
          <SettingsLabel>Maths Proficiencies</SettingsLabel>
          <CheckableMulti
            name={`proficiencies-${index}`}
            canDeselectAll
            options={PROFICIENCIES}
            selected={selectedProficiencies}
            onSelect={values => {
              setSelectedProficiencies(values);
            }}
          />
        </SettingsGroup>
      )}

      <Box $justifyContent="space-between" $alignItems="center" $marginTop={16}>
        <LinkButton
          type="button"
          data-testid="cancel-content-point-group-edit"
          onClick={handleCancel}
        >
          Cancel
        </LinkButton>
        <PrimaryButton
          type="button"
          $isSmall
          data-testid="save-content-point-group"
          loading={!!isSaving && !isDeleteModalVisible && !isRemoveModalVisible}
          disabled={!!isSaving || !selectedOutcome}
          aria-label={`Save group: ${selectedOutcome?.label}`}
          onClick={handleSave}
        >
          Save
        </PrimaryButton>
      </Box>
    </SettingsGroup>
  );
};

export default ContentPointGroupEdit;
